import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { standalone } from '../api/methods';
export const cardKeys = {
    all: ['cards'],
    myCards: (limit) => ['cards', 'mine', limit],
    detail: (cardId) => ['cards', 'detail', cardId],
    search: (query, includeArchived) => ['cards', 'search', query, includeArchived],
    workspaceSearch: (query, includeArchived, limit) => ['cards', 'workspace-search', query, includeArchived, limit],
    boards: ['boards'],
    boardCards: (boardId, listName) => ['boards', boardId, 'cards', listName],
    boardMirror: (boardId) => ['boards', boardId, 'mirror'],
};
export function useCard(cardId) {
    return useQuery({
        queryKey: cardKeys.detail(cardId),
        queryFn: () => standalone.getCard(cardId),
        enabled: !!cardId,
    });
}
export function useSearchCards(query, includeArchived = true, limit = 50) {
    return useQuery({
        queryKey: cardKeys.search(query, includeArchived),
        queryFn: () => standalone.searchCards(query, includeArchived, limit),
        enabled: query.length >= 2,
    });
}
/**
 * Global search returning both card and ticket-number matches (#846).
 * Use this on the /search page; useSearchCards stays the Card[]-only picker.
 */
export function useWorkspaceSearch(query, includeArchived = true, limit = 50) {
    return useQuery({
        queryKey: cardKeys.workspaceSearch(query, includeArchived, limit),
        queryFn: () => standalone.searchWorkspace(query, includeArchived, limit),
        enabled: query.length >= 2,
    });
}
export function useBoards() {
    return useQuery({
        queryKey: cardKeys.boards,
        queryFn: () => standalone.getBoards(),
        staleTime: 5 * 60 * 1000,
    });
}
export function useBoardMirror(boardId) {
    return useQuery({
        queryKey: cardKeys.boardMirror(boardId),
        queryFn: () => standalone.getBoardMirror(boardId),
        enabled: !!boardId,
        staleTime: 30 * 1000,
    });
}
export function useMoveTrelloCard(boardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ cardId, targetListId, targetListName, moveChildTicketsBackward, }) => standalone.moveTrelloCard(boardId, cardId, {
            targetListId,
            targetListName,
            moveChildTicketsBackward,
        }),
        onMutate: async ({ cardId, targetListId, targetListName }) => {
            const key = cardKeys.boardMirror(boardId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData(key);
            if (previous) {
                const targetList = previous.lists.find((list) => targetListId
                    ? String(list.id) === String(targetListId)
                    : list.name === targetListName);
                const sourceList = previous.lists.find((list) => list.cards.some((card) => String(card.id) === String(cardId)));
                const moved = sourceList?.cards.find((card) => String(card.id) === String(cardId));
                if (targetList && sourceList && moved && targetList.id !== sourceList.id) {
                    queryClient.setQueryData(key, {
                        ...previous,
                        lists: previous.lists.map((list) => {
                            if (list.id === sourceList.id) {
                                return {
                                    ...list,
                                    cards: list.cards.filter((card) => String(card.id) !== String(cardId)),
                                };
                            }
                            if (list.id === targetList.id) {
                                return {
                                    ...list,
                                    cards: [...list.cards, { ...moved, listId: targetList.id }],
                                };
                            }
                            return list;
                        }),
                    });
                }
            }
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(cardKeys.boardMirror(boardId), context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['boardSites', boardId] });
            queryClient.invalidateQueries({ queryKey: cardKeys.boardMirror(boardId) });
            queryClient.invalidateQueries({ queryKey: cardKeys.boardCards(boardId) });
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
        },
    });
}
export function useMoveSiteToList(boardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ siteId, targetListName }) => standalone.moveSiteToList(boardId, siteId, targetListName),
        // Optimistically relocate the card in the board-mirror cache so a drag/move
        // lands instantly. Without this the card snaps back to its origin column
        // until the post-success refetch returns. Server truth (list position,
        // ticket counts, status forward-sync) is reconciled in onSettled.
        onMutate: async ({ siteId, targetListName }) => {
            const key = cardKeys.boardMirror(boardId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData(key);
            if (previous) {
                const targetList = previous.lists.find((l) => l.name === targetListName);
                // Match BoardPage's card→site id mapping: String(card.siteId || card.id).
                const sourceList = previous.lists.find((l) => l.cards.some((c) => String(c.siteId || c.id) === siteId));
                if (targetList && sourceList && sourceList.name !== targetListName) {
                    const moved = sourceList.cards.find((c) => String(c.siteId || c.id) === siteId);
                    if (moved) {
                        queryClient.setQueryData(key, {
                            ...previous,
                            lists: previous.lists.map((list) => {
                                if (list.name === sourceList.name) {
                                    return {
                                        ...list,
                                        cards: list.cards.filter((c) => String(c.siteId || c.id) !== siteId),
                                    };
                                }
                                if (list.name === targetListName) {
                                    return {
                                        ...list,
                                        cards: [...list.cards, { ...moved, listId: list.id }],
                                    };
                                }
                                return list;
                            }),
                        });
                    }
                }
            }
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(cardKeys.boardMirror(boardId), context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['boardSites', boardId] });
            queryClient.invalidateQueries({ queryKey: cardKeys.boardMirror(boardId) });
        },
    });
}
//# sourceMappingURL=useCards.js.map